import type { AnyComment } from './types';

import { US_STATES } from '@/constants/states';

/**
 * Whether a comment hides its author. Every comment carries the flag, but an
 * optimistic one built before the API answered may not, which reads as named.
 */
export const getCommentIsAnonymous = (comment: AnyComment): boolean =>
  'isAnonymous' in comment ? comment.isAnonymous : false;

export const getInitials = (firstName: string, lastName: string) =>
  `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

/** "Anonymous user", numbered when the thread numbers its anonymous authors. */
export const getAnonymousName = (anonymousAlias?: number | null): string =>
  anonymousAlias ? `Anonymous user ${anonymousAlias}` : 'Anonymous user';

/** "Anonymous user" for an anonymous comment or an author with no name. */
export const getDisplayName = (
  firstName: string | null,
  lastName: string | null,
  isAnonymous: boolean,
  anonymousAlias?: number | null
): string => {
  if (isAnonymous) {
    return getAnonymousName(anonymousAlias);
  }

  if (!firstName && !lastName) {
    return 'Anonymous user';
  }

  return `${firstName || ''} ${lastName || ''}`.trim();
};

/** A state's name from its abbreviation, the abbreviation if it is unknown, empty for none. */
export const getStateName = (abbr?: string | null): string => {
  if (!abbr) {
    return '';
  }

  const state = US_STATES.find((entry) => entry.abbr === abbr);

  return state ? state.name : abbr;
};

/**
 * Who a reply answers, for its "@name" lead-in. Null for a top-level comment. "Anonymous user"
 * when the comment it answers is anonymous or its author has no name.
 */
export const getReplyToName = (
  comment: AnyComment,
  isReply: boolean,
  commentMap?: Map<number, AnyComment>
): string | null => {
  if (!isReply || !comment.replyToUser || !comment.replyToId) {
    return null;
  }

  const repliedToComment = commentMap?.get(comment.replyToId);

  const isRepliedToAnonymous = repliedToComment && getCommentIsAnonymous(repliedToComment);

  if (isRepliedToAnonymous) {
    return getAnonymousName(repliedToComment.anonymousAlias);
  }

  const { firstName, lastName } = comment.replyToUser;

  const hasName = firstName && lastName;

  if (!hasName) {
    return 'Anonymous user';
  }

  return `${firstName} ${lastName}`;
};
